import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/ImageUpload";
import ScheduleEditor from "@/components/team/ScheduleEditor";
import { PROFESSIONALS } from "@/constants/testIds";

const EMPTY_FORM = {
  name: "", specialty: "", photo_url: null, schedule: [],
  accessMode: "none", link_user_id: "", login_email: "", login_password: "",
  show_on_landing: false, is_featured_doctor: false, bio: "",  // Fix AZ (2026-10-30): pagina web
};
const PROFESSIONAL_PLACEHOLDER = "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&q=85";

/** Crea o edita un Profesional (quien puede atender turnos). A diferencia de
 * Equipo, un Profesional no necesita cuenta de login: puede quedar
 * "suelto" (ej. personal que no usa el sistema), vincularse a una cuenta
 * existente (ej. el Admin/dueña de la clinica) o crear una cuenta nueva en
 * el mismo paso ("¿Querés que además pueda entrar al sistema?"). */
export default function ProfessionalModal({ open, onOpenChange, professional, linkableUsers, onSubmit, onUnlink }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(professional);

  useEffect(() => {
    if (professional) {
      setForm({
        name: professional.name || "",
        specialty: professional.specialty || "",
        photo_url: professional.photo_url || null,
        schedule: professional.schedule || [],
        accessMode: "none", link_user_id: "", login_email: "", login_password: "",
        show_on_landing: Boolean(professional.show_on_landing),
        is_featured_doctor: Boolean(professional.is_featured_doctor),
        bio: professional.bio || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [professional, open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSubmit(form, professional);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={PROFESSIONALS.modal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar profesional" : "Nuevo profesional"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ImageUpload
            currentUrl={form.photo_url}
            onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
            placeholderUrl={PROFESSIONAL_PLACEHOLDER}
            testId={PROFESSIONALS.modalPhotoUpload}
          />

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre completo</Label>
            <Input value={form.name} onChange={set("name")} data-testid={PROFESSIONALS.modalNameInput} placeholder="Ej: Dra. Isabella Fernandez" />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Especialidad</Label>
            <Input value={form.specialty} onChange={set("specialty")} data-testid={PROFESSIONALS.modalSpecialtyInput} placeholder="Ej: Dermatologia Estetica" />
          </div>

          <ScheduleEditor value={form.schedule} onChange={(s) => setForm((f) => ({ ...f, schedule: s }))} />

          {/* Fix AZ (2026-10-30): como aparece en la pagina web */}
          <div className="rounded-lg border border-border p-3 space-y-3">
            <p className="text-sm font-medium text-foreground">Página web</p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox className="mt-0.5" checked={form.show_on_landing} onCheckedChange={(v) => setForm((f) => ({ ...f, show_on_landing: v === true }))} data-testid={PROFESSIONALS.modalShowOnLanding} />
              <span>Mostrar en la sección "Nuestro equipo" <span className="block text-xs text-muted-foreground">Con su foto, nombre y especialidad.</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox className="mt-0.5" checked={form.is_featured_doctor} onCheckedChange={(v) => setForm((f) => ({ ...f, is_featured_doctor: v === true }))} data-testid={PROFESSIONALS.modalFeaturedDoctor} />
              <span>Es la doctora principal <span className="block text-xs text-muted-foreground">Aparece en la sección "La Dra. Strani", con su reseña. Solo puede haber una: si marcás otra, esta se desmarca.</span></span>
            </label>
            {(form.show_on_landing || form.is_featured_doctor) && (
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Reseña para la página (opcional)</Label>
                <Textarea value={form.bio} onChange={set("bio")} rows={3} maxLength={600} placeholder="2 o 3 líneas sobre su trayectoria" data-testid={PROFESSIONALS.modalBioInput} />
              </div>
            )}
          </div>

          {isEdit && professional?.linked_user ? (
            <div className="rounded-lg border border-border bg-background px-3 py-2.5 flex items-center justify-between gap-3">
              <div className="text-sm">
                <p className="text-foreground font-medium">{professional.linked_user.name}</p>
                <p className="text-muted-foreground text-xs">{professional.linked_user.email} &middot; acceso al sistema</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => onUnlink(professional)} data-testid={PROFESSIONALS.modalUnlinkButton}>
                Desvincular
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">¿Querés que además pueda entrar al sistema?</Label>
                <Select value={form.accessMode} onValueChange={(v) => setForm((f) => ({ ...f, accessMode: v }))}>
                  <SelectTrigger data-testid={PROFESSIONALS.modalAccessModeSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No, sin acceso al sistema</SelectItem>
                    <SelectItem value="existing">Vincular una cuenta que ya existe</SelectItem>
                    <SelectItem value="new">Sí, crear una cuenta nueva</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.accessMode === "existing" && (
                <div>
                  <Select value={form.link_user_id} onValueChange={(v) => setForm((f) => ({ ...f, link_user_id: v }))}>
                    <SelectTrigger data-testid={PROFESSIONALS.modalLinkUserSelect}>
                      <SelectValue placeholder="Elegí una cuenta" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkableUsers.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No hay cuentas disponibles para vincular.</div>
                      )}
                      {linkableUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Ej: vinculá al Admin si tambien atiende turnos.</p>
                </div>
              )}

              {form.accessMode === "new" && (
                <div className="space-y-2">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-1 block">Email de acceso</Label>
                    <Input type="email" value={form.login_email} onChange={set("login_email")} data-testid={PROFESSIONALS.modalLoginEmailInput} placeholder="correo@stranijustbecause.com" />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-1 block">Contraseña temporal</Label>
                    <Input type="password" value={form.login_password} onChange={set("login_password")} data-testid={PROFESSIONALS.modalLoginPasswordInput} placeholder="Mínimo 6 caracteres" />
                  </div>
                  <p className="text-xs text-muted-foreground">Se crea con rol Profesional.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={PROFESSIONALS.modalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={PROFESSIONALS.modalSaveButton}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
